import {CSSProperties, forwardRef, ReactNode, useEffect, useState} from "react";
import classNames from "classnames";

import {isEqual} from "@pg-mono/nodash";

import {Checkbox, ICheckboxProps} from "./Checkbox";

import {checkboxList} from "./CheckboxGroup.module.css";

export interface ICheckboxGroupProps {
    name: string;
    options: ICheckboxGroupOption[];
    value?: ICheckboxGroupOption[];
    preSelectedOptions?: ICheckboxGroupOption["value"][];
    className?: string;
    checkBoxClassName?: string;
    checkboxListClassName?: string;
    checkboxRender?: (props: ICheckboxProps & {style?: CSSProperties}) => ReactNode;
    onChange?: (selectedOptions: ICheckboxGroupOption[]) => void;
    style?: CSSProperties;
    checkedMarkColor?: string;
}

export interface ICheckboxGroupOption<T extends string = string> {
    value: T;
    labelContent: ReactNode | string;
}

export const CheckboxGroup = forwardRef<HTMLInputElement, ICheckboxGroupProps>((props, ref) => {
    const {name, options, preSelectedOptions, checkboxRender, onChange, value: valuesArray} = props;
    const [chosenOptions, setChosenOptions] = useState<ICheckboxGroupOption[]>(
        preSelectedOptions ? options.filter((option) => preSelectedOptions.includes(option.value)) : []
    );
    const checkboxListCN = classNames(checkboxList, props.className, props.checkboxListClassName);
    const checkboxCN = classNames(checkboxList, props.className, props.checkBoxClassName);

    useEffect(() => {
        if (onChange) {
            onChange(chosenOptions);
        }
    }, [chosenOptions]);

    useEffect(() => {
        //handling change chosenOptions on form reset witch reset method
        if (
            valuesArray &&
            chosenOptions &&
            !isEqual(
                chosenOptions.map((el) => el.value),
                valuesArray
            )
        ) {
            setChosenOptions(valuesArray);
        }
    }, [valuesArray]);

    const handleCheckboxChange = (name: string, checked: boolean, value: string) => {
        if (isCheckboxSelected(chosenOptions, value)) {
            setChosenOptions(chosenOptions.filter((option) => option.value !== value));
        } else {
            const foundOption = options.find((option) => option.value === value) as ICheckboxGroupOption;
            setChosenOptions([...chosenOptions, foundOption]);
        }
    };
    ``;
    return (
        <ul className={checkboxListCN} style={props.style}>
            {options.map(({value, labelContent}) => {
                const checkboxId = `${name}_${value}`;

                return (
                    <li key={`${name}_${value}`}>
                        {checkboxRender ? (
                            checkboxRender({
                                name: checkboxId,
                                id: checkboxId,
                                value,
                                onChange: (name, checked, value) => {
                                    handleCheckboxChange(name, checked, value as string);
                                },
                                checked: isCheckboxSelected(chosenOptions, value),
                                labelContent
                            })
                        ) : (
                            <Checkbox
                                name={checkboxId}
                                id={checkboxId}
                                value={value}
                                checked={isCheckboxSelected(chosenOptions, value)}
                                onChange={handleCheckboxChange}
                                labelContent={labelContent}
                                className={checkboxCN}
                                ref={ref}
                                checkedMarkColor={props.checkedMarkColor}
                            />
                        )}
                    </li>
                );
            })}
        </ul>
    );
});

//  Utils
function isCheckboxSelected(options: ICheckboxGroupOption[], newOptionValue: ICheckboxGroupOption["value"]) {
    return Boolean(options.find((option) => option.value === newOptionValue));
}
